import { useEffect, useState } from 'react';
import { Eye, X } from 'lucide-react';
import { setEphemeralChannelToken } from '@/lib/graphql/client';
import {
  readTaliImpersonationSession,
  resolveTaliImpersonationOrigin,
  TALI_IMPERSONATION_READY,
  type TaliImpersonationSession,
} from '@tali/platform-sdk';

const IMPERSONATION_KEY = 'tali_impersonation';

type ImpersonationState = TaliImpersonationSession;

/**
 * Receives an origin-checked impersonation session from the platform console
 * and shows a fixed banner. Tenant credentials never enter the URL.
 *
 * Flow:
 * 1. Platform ops console opens the merchant dashboard and waits for a ready signal
 * 2. This component accepts the session only from the opening platform origin
 * 3. Banner stays visible until user clicks "Exit" (closes tab) or clears session
 */
function useImpersonation() {
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);

  useEffect(() => {
    const opener = window.opener;
    const expectedOrigin = resolveTaliImpersonationOrigin(
      import.meta.env.VITE_TALI_PLATFORM_URL,
      document.referrer,
    );

    const receiveSession = (event: MessageEvent) => {
      if (!opener || !expectedOrigin) return;
      const session = readTaliImpersonationSession(event, opener, expectedOrigin);
      if (!session) return;
      const state: ImpersonationState = {
        tenantToken: session.tenantToken,
        ...(session.storeName === undefined ? {} : { storeName: session.storeName }),
      };
      sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
      setEphemeralChannelToken(state.tenantToken);
      setImpersonation(state);
      window.removeEventListener('message', receiveSession);
    };

    if (opener && expectedOrigin) {
      window.addEventListener('message', receiveSession);
      opener.postMessage({ type: TALI_IMPERSONATION_READY }, expectedOrigin);
    }

    // Check sessionStorage for existing impersonation
    const stored = sessionStorage.getItem(IMPERSONATION_KEY);
    if (stored) {
      try {
        const state = JSON.parse(stored) as Partial<ImpersonationState>;
        if (typeof state.tenantToken !== 'string' || state.tenantToken.trim().length === 0) {
          throw new Error('Invalid impersonation session');
        }
        const restored: ImpersonationState = {
          tenantToken: state.tenantToken,
          ...(typeof state.storeName === 'string' ? { storeName: state.storeName } : {}),
        };
        setEphemeralChannelToken(restored.tenantToken);
        setImpersonation(restored);
      } catch {
        sessionStorage.removeItem(IMPERSONATION_KEY);
        setEphemeralChannelToken(null);
      }
    }
    return () => window.removeEventListener('message', receiveSession);
  }, []);

  const exitImpersonation = () => {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    setEphemeralChannelToken(null);
    setImpersonation(null);
    // Close the tab (it was opened by the platform console)
    window.close();
    // Fallback: if window.close() is blocked, redirect to a blank page
    window.location.href = 'about:blank';
  };

  return { impersonation, exitImpersonation };
}

export function ImpersonationBanner() {
  const { impersonation, exitImpersonation } = useImpersonation();

  if (!impersonation) return null;

  return (
    <div className="sticky top-0 z-[60] flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-white ">
      <Eye className="size-4 shrink-0" />
      <span>
        Platform Ops — Viewing merchant store
        {impersonation.storeName ? `: ${impersonation.storeName}` : ''}
      </span>
      <button
        onClick={exitImpersonation}
        className="ms-2 inline-flex items-center gap-1 rounded-md bg-white/20 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-white/30"
      >
        <X className="size-3" />
        Exit
      </button>
    </div>
  );
}
